/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React, {
  FormEvent,
  ReactNode,
  ChangeEvent,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { _User } from '../../_shared/interfaces/user';
import { Input, Alert, message } from 'antd';
import { withRouter, RouteComponentProps } from 'react-router';
import styles from './edit-preset.styles';
import { Button } from '../../_shared/button';
import Link from '../../_shared/link';
import { set } from 'lodash';
import SharedStyles from '../../_shared/styles';
import { getEquipments } from '../../_shared/services/manage-equipment.service';
import { _Location, Location } from '../../_shared/interfaces/location';
import { Opt } from '../../_shared/interfaces/opt';
import { SelectValue } from 'antd/lib/select';
import { _Preset } from '../../_shared/interfaces/preset';
import colors from '../../_shared/colors';
import Label from '../../_shared/label';
import { TableTitleHeader } from '../../_shared/table-list';
import { uppercaseWords } from '../../utils';
import {
  IHandpiecePreset,
  HandpieceEndoSetting,
  humanReadableEndoSetting,
  humanReadableGearRatio,
  rpmOptionsForGearRatio,
  torqueOptionsForGearRatio,
  HandpieceProfileKey,
  HandpiecePresetKey,
  IHandpieceProfile,
} from '../../_shared/interfaces/handpieceProfiles';
import {
  setHandpiecePresetOverride,
  syncHandpiecesForLocation,
} from '../../_shared/services/handpiece-profiles.service';
import { Org } from '../../_shared/interfaces/org';
import { AIcon as Icon } from '../../_shared/icon';
import { Form, WithForm } from '../../_shared/form';

interface IProps {
  updatePresets: () => Promise<void>;
  backText: string;
  preset: IHandpiecePreset;
  profile: IHandpieceProfile;
  profileKey: HandpieceProfileKey;
  presetKey: HandpiecePresetKey;
  // tslint:disable-next-line:no-any
  history: any;
  org: Org;
  location: Location;
}

interface TDisplay {
  title?: string;
  var: string;
  style?: string;
  canDisplay?: () => boolean;
  transform?: (value: string) => string;
  options?: any;
  data?: () => string[];
  opts?: Opt[];
  type?: string;
  value?: string;
  label?: string;
  subText?: (item: TDisplay) => string;
  onChange?: (e: FormEvent | number | undefined, variable: string) => void;
  onSelect?: (value: SelectValue) => void;
  disabled?: boolean;
  valueType?: string;
  suffix?: string | ReactNode;
  suffixIcon?: ReactNode;
}

const _EditPresetComponent: React.FC<RouteComponentProps & { form?: any }> = props => {
  const { history, form } = props as any;
  const {
    preset,
    org,
    location,
    profile,
    profileKey,
    presetKey,
    backText,
  } = props.location.state as IProps;

  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(preset.name);
  const [saving, setSaving] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [workingPreset, setWorkingPreset] = useState<IHandpiecePreset>(preset);
  const [isEndoPreset] = useState<boolean>(
    preset && preset.endo_mode_enum_raw !== HandpieceEndoSetting.autoOff
  );

  const tableData: Array<TDisplay[]> = useMemo(() => {
    const gearRatioOptions = [
      { label: humanReadableGearRatio(0), value: 0 },
      { label: humanReadableGearRatio(1), value: 1 },
      isEndoPreset ? { label: humanReadableGearRatio(2), value: 2 } : undefined,
      isEndoPreset ? { label: humanReadableGearRatio(3), value: 3 } : undefined,
      isEndoPreset ? { label: humanReadableGearRatio(4), value: 4 } : undefined,
      isEndoPreset ? { label: humanReadableGearRatio(5), value: 5 } : undefined,
    ].filter(o => o !== undefined) as Opt[];

    const rpmOptions = rpmOptionsForGearRatio(
      workingPreset.gear_ratio_enum_raw
    ).map(i => ({ label: i, value: i }));
    const torqueOptions = torqueOptionsForGearRatio(
      workingPreset.gear_ratio_enum_raw
    ).map(i => ({ label: i, value: i }));

    return [
      [
        {
          title: 'Gear Ratio',
          var: 'gear_ratio_enum_raw',
          type: 'dropdown',
          opts: gearRatioOptions,
          options: {
            initialValue: humanReadableGearRatio(
              workingPreset.gear_ratio_enum_raw
            ),
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'Gear ratio is required',
              },
            ],
          },
        },
        {
          title: 'Head Speed',
          var: 'rpm',
          type: 'dropdown',
          opts: rpmOptions,
          options: {
            initialValue: workingPreset.rpm,
            validateTrigger: 'onChange',
            rules: [
              {
                required: true,
                message: 'Speed is required',
              },
            ],
          },
        },
        {
          title: 'Direction of Rotation',
          type: 'dropdown',
          var: 'rotation_clockwise',
          opts: [
            { value: 1, label: 'Clockwise' },
            { value: 0, label: 'Counterclockwise' },
          ],
          options: {
            initialValue: workingPreset.rotation_clockwise
              ? 'Clockwise'
              : 'Counterclockwise',
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'Rotation Direction is required',
              },
            ],
          },
        },
        {
          title: 'Endo Settings',
          var: 'endo_mode_enum_raw',
          type: 'dropdown',
          canDisplay: () => isEndoPreset,
          options: {
            initialValue: humanReadableEndoSetting(
              workingPreset.endo_mode_enum_raw
            ),
          },
          opts: [
            { label: humanReadableEndoSetting(0), value: 0 },
            { label: humanReadableEndoSetting(1), value: 1 },
            { label: humanReadableEndoSetting(2), value: 2 },
          ],
        },
        {
          title: 'Torque Limit',
          var: 'torque_threshold',
          type: 'dropdown',
          canDisplay: () => isEndoPreset,
          opts: torqueOptions,
          options: {
            initialValue: workingPreset.torque_threshold,
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'Torque is required',
              },
            ],
          },
        },
      ],
    ];
  }, [workingPreset]);

  // AER-1704: this effect used to pull EVERY piece of equipment in the
  // organization (`dentalOrgId: { eq: location.orgId }`) purely so that Save
  // could loop over it, which is how one location's settings ended up being
  // pushed to handpieces at other locations. Save now delegates to
  // `syncHandpiecesForLocation`, which resolves the handpieces at the selected
  // location itself, so nothing org-wide is read here any more. The (now
  // location-scoped) call is kept because it is what holds the form in its
  // loading state until the equipment store has settled -- `getEquipments`
  // waits that out internally.
  useEffect(() => {
    const work = async () => {
      setLoading(true);
      await getEquipments({ locationId: { eq: location.id } });
      setLoading(false);
    };
    work();
  }, []);

  useEffect(() => {
    if (name) {
      workingPreset.name = name;
      setWorkingPreset({ ...workingPreset });
    }
  }, [name]);

  const submit = useCallback(
    async (e?: MouseEvent | FormEvent) => {
      e && e.preventDefault();
      if (loading) {
        return;
      }

      setSaving(true);
      try {
        // AER-1719: the outcome of the override write was discarded, so a preset
        // that never reached the database still ended on the "sent to handpiece
        // unit(s)" toast. Never push after a failed write either -- the devices
        // would be re-sent the OLD values while the screen shows the new ones.
        const stored = await setHandpiecePresetOverride(
          location,
          workingPreset,
          profileKey,
          presetKey
        );
        if (!stored) {
          message.error('Preset could not be saved.');
          return;
        }

        // AER-1704: sync only the handpieces at the location being edited.
        // The preset override that was just saved lives on this location, and
        // the server builds each handpiece's payload from the location alone,
        // so handpieces at sibling locations must not be touched.
        //
        // AER-1719: and read what comes back. This used to call `goBack(true)`
        // unconditionally: `syncHandpiecesForLocation` reports failures in its
        // return value and never throws, so the catch below could not fire and
        // every failed push was announced as "Preset updated and sent to
        // handpiece unit(s)". The old `if (location.orgId)` guard is gone too --
        // it silently did nothing at all when the location had no organization;
        // the helper reports that case as an error like any other.
        const result = await syncHandpiecesForLocation(location);

        // `failed` names the handpieces that are NOT configured the way this screen
        // now shows them; `error` is the reason a sync could not be completed at all.
        // Report the devices whenever we know which ones they are -- a save where two
        // of three units are offline is a partial failure and has to read as one --
        // and fall back to the reason when there are no ids to name.
        if (result.failed.length > 0) {
          const total = result.failed.length + result.synced.length;
          message.error(
            result.synced.length > 0
              ? `Preset saved, but ${result.failed.length} of ${total} handpiece unit(s) did not receive it and are still using the previous settings.`
              : 'Preset saved, but it could not be sent to the handpiece unit(s), which are still using the previous settings.'
          );
          return;
        }
        if (result.error) {
          message.error('Preset saved, but it could not be sent to the handpiece unit(s), which are still using the previous settings.');
          return;
        }

        // Stay on this screen for both failures above rather than returning with
        // a success toast: the override IS saved, so pressing Save again simply
        // retries the push.
        //
        // AER-1719: a location with no handpieces syncs nothing, so do not claim
        // the preset was sent anywhere.
        goBack(true, result.synced.length > 0 ? undefined : 'Preset updated.');
      } catch (err) {
        message.error(err.message);
      } finally {
        setSaving(false);
      }
    },
    [workingPreset]
  );

  const onChange = useCallback(
    (value: SelectValue | number | undefined, variable: string) => {
      set(workingPreset, variable, value);

      if (variable === 'gear_ratio_enum_raw') {
        const rpmOptions = rpmOptionsForGearRatio(
          workingPreset.gear_ratio_enum_raw
        );
        set(workingPreset, 'rpm', rpmOptions[0]);
        const torqueOptions = torqueOptionsForGearRatio(
          workingPreset.gear_ratio_enum_raw
        );
        set(workingPreset, 'torque_threshold', torqueOptions[0]);

        // AER-1706: the cascade above only rewrote `workingPreset`. What is on
        // screen comes from AntD's own form store -- each field is declared with
        // `options.initialValue`, which is applied once, when the field mounts --
        // so Head Speed and Torque Limit carried on displaying the *previous*
        // gear ratio's numbers (e.g. 300 rpm still shown after switching to 1:5,
        // a value that ratio cannot even offer, and 0.6 Nm still shown after
        // switching to 5:1, whose range starts at 1.5). Save posts
        // `workingPreset`, so the hidden value silently won. AER-1613 wired up
        // only the form -> workingPreset direction; push the programmatic reset
        // back into the form store too so the two cannot drift.
        // `setFieldsValue` does not re-fire `onValuesChange`, so this cannot
        // recurse back into `onChange`.
        if (form && typeof form.setFieldsValue === 'function') {
          form.setFieldsValue({
            rpm: rpmOptions[0],
            torque_threshold: torqueOptions[0],
          });
        }
      }

      setWorkingPreset({ ...workingPreset });
    },
    [workingPreset, form]
  );

  // AER-1613: the migrated AntD form writes edited Gear Ratio / Head Speed /
  // Direction / Endo / Torque values only into AntD's internal form store, so
  // they never reached `workingPreset` and Save posted the unchanged preset.
  // Route each *changed* field (keyed by `item.var`, value = the numeric
  // `opt.value`) through the existing `onChange` logic so it lands on
  // `workingPreset` (and the gear-ratio cascade still fires). Untouched fields
  // are left alone so their already-correct typed values are preserved.
  const onFormValuesChange = useCallback(
    (_all: Record<string, SelectValue>, changed: Record<string, SelectValue>) => {
      Object.keys(changed || {}).forEach(key => onChange(changed[key], key));
    },
    [onChange]
  );

  // AER-1719: `successMessage` is rendered by manage-profile as a green success
  // toast, so it may only ever describe something that actually happened. The
  // optional override exists for the save that stored the preset but had no
  // handpiece to send it to.
  const goBack = useCallback((edited: boolean, successMessage?: string) => {
    history.replace('/dashboard/manageprofile', {
      selected: {
        dentalOrg: org,
        location: location,
        profile: profile,
        profileKey: profileKey,
        presetType: presetKey[0] === 'e' ? 'endo' : 'prep',
        presetKey: presetKey,
        preset: workingPreset,
      },
      successMessage: edited
        ? successMessage || 'Preset updated and sent to handpiece unit(s)'
        : undefined,
    });
  }, []);

  const onChangeName = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const val = uppercaseWords(event.target.value);
    if (!val) {
      setNameError('Name is required');
    } else {
      setNameError(undefined);
    }
    setName(val);
  }, []);

  const renderTopForm = useMemo(() => {
    if (loading) {
      return <Icon type="loading" />;
    }

    return (
      <div css={css(styles.topFormContainer)}>
        <div>
          <Label
            required
            label="Name"
            customCss={`font-size: 12px; font-weight: bold;`}
          />
          <Input
            maxLength={16}
            value={name}
            onChange={onChangeName}
            css={css(`margin-top: -5px;`)}
          />
          {nameError && (
            <span css={css(`color: ${colors.error}; margin-top: -100px;`)}>
              {nameError}
            </span>
          )}
        </div>
      </div>
    );
  }, [name]);

  // AER-1613: antd v5 `getFieldsError()` returns an array of
  // `{ name, errors, warnings }` (the v3 record-shape iteration below was
  // also leftover from the old Form.create wrapping that was dropped during
  // the antd 5/6 migration).
  const hasFieldError = (): boolean => {
    if (!form || typeof form.getFieldsError !== 'function') {
      return false;
    }
    const fieldErrors: Array<{ errors: string[] }> = form.getFieldsError();
    return (
      Array.isArray(fieldErrors) &&
      fieldErrors.some(f => f && f.errors && f.errors.length > 0)
    );
  };

  // AER-1706: render through the form instance `WithForm` hands us
  // (`form.Form`) rather than the bare shared `Form` -- the same pattern
  // edit-profile and edit-model already use. Rendering `<Form>` directly made it
  // fall back to building its OWN internal AntD instance (`props.form || _form`
  // in `_shared/form`), which left the `form` prop this component holds bound to
  // nothing: `form.setFieldsValue()` in the gear-ratio cascade above -- and
  // `form.getFieldsError()` in `hasFieldError()` -- would have been silent
  // no-ops against an orphan store. The fallback to the imported `Form` keeps
  // the component renderable if it is ever used unwrapped.
  const renderForm = useMemo(() => {
    const WForm = (form && form.Form) || Form;
    return (
      <WForm
        layout="vertical"
        css={css(styles.formContainer)}
        onSubmit={submit}
        onValuesChange={onFormValuesChange}
        items={tableData as any}
      />
    );
  }, [workingPreset]);

  const renderButtons = () => {
    const cancel = (
      <Button
        title={'Cancel'}
        disabled={loading}
        css={css`
          margin: 0 5px;
        `}
        onClick={() => goBack(false)}
      />
    );

    const submitButton = (
      <Button
        title={'Save'}
        loading={saving || loading}
        css={css`
          margin: 0 5px;
        `}
        disabled={saving || loading || !!nameError || hasFieldError()}
        onClick={submit}
      />
    );

    return (
      <div css={css(SharedStyles.row, `margin-top: 50px; align-self: center;`)}>
        {cancel}
        {submitButton}
      </div>
    );
  };

  return (
    <div css={css(styles.container)}>
      {!!backText && (
        <Link
          css={css`
            margin-bottom: 5px;
            margin-right: auto;
          `}
          onClick={() => goBack(false)}
        >
          {backText}
        </Link>
      )}

      <div css={css(SharedStyles.row, styles.rowMargin)}>
        <TableTitleHeader>
          {'Edit Preset'}
          {loading && <Icon css={css('margin-left: 5px;')} type="loading" />}
        </TableTitleHeader>
      </div>
      <div css={css(SharedStyles.hr)} />

      {workingPreset && renderTopForm}
      {workingPreset && renderForm}
      {workingPreset && renderButtons()}
    </div>
  );
};

// AER-1613: wrap with WithForm so the component receives a real antd form
// instance via the `form` prop. Without this, `form.getFieldsError()` in
// `hasFieldError()` below throws and the page renders as a white screen.
export const EditPresetComponent = WithForm(withRouter(_EditPresetComponent));
